// Kleine Bausteine, die mehrere Seiten des CRM brauchen: der Stand einer
// Besichtigung als Chip und die Rückfrage vor einem
// Schritt, der nur einmal geht („Freigeben? Freigeben geht nur einmal.“).

import { useEffect, useRef, type ReactNode } from "react";
import { Chip, Ikon, type ChipArt, type IkonName } from "@happy-energy/oberflaeche";
import { STAND_SATZ, STAND_TEXT, type StandArt } from "./anzeige.ts";

/** Die Farbe je Stand: „wartet auf Freigabe“ in Lime, Nachtrag und „bitte ansehen“ in der Signalfarbe Orange, freigegeben grün. */
const STAND_CHIP: Record<StandArt, { art?: ChipArt; ikon?: IkonName }> = {
  neu: { art: "lime" },
  nachtrag: { art: "warn", ikon: "plus" },
  "bitte-ansehen": { art: "warn", ikon: "auge" },
  freigegeben: { art: "ok", ikon: "haken" },
  "beim-buero": { ikon: "uhr" },
  "nicht-gesendet": { ikon: "uhr" },
};

/**
 * Der Stand als Chip, mit dem einen Satz dazu, wenn die Maus darauf steht.
 * Der Satz hängt an einer Hülle, weil der Chip aus oberflaeche keinen
 * `title` kennt; die Hülle lässt den Chip umbrechen, wo der Platz knapp ist
 * (Karten im Weg), statt ihn abzuschneiden.
 */
export function StandChip({ stand }: { stand: StandArt }) {
  const c = STAND_CHIP[stand];
  return (
    <span className="stand-chip" title={STAND_SATZ[stand]}>
      <Chip {...(c.art ? { art: c.art } : {})} {...(c.ikon ? { ikon: c.ikon } : {})}>
        {STAND_TEXT[stand]}
      </Chip>
    </span>
  );
}

/**
 * Die Rückfrage vor einem Schritt, der nur einmal geht – ein eigenes
 * Fenster über der Seite (Aussehen aus marke.css, „nachfrage“). Esc oder
 * „Abbrechen“ schließt; der Hauptknopf hat gleich den Fokus, Enter bestätigt.
 */
export function Nachfrage({
  titel,
  children,
  jaText,
  ja,
  neinText = "Abbrechen",
  nein,
  ikon = "info",
  beschaeftigt = false,
  gefahr = false,
}: {
  titel: string;
  children?: ReactNode;
  jaText: string;
  ja: () => void;
  neinText?: string;
  nein: () => void;
  ikon?: IkonName;
  beschaeftigt?: boolean;
  /** Rot statt Lime – für „verwerfen“. */
  gefahr?: boolean;
}) {
  const jaKnopf = useRef<HTMLButtonElement>(null);
  const neinAktuell = useRef(nein);
  useEffect(() => {
    neinAktuell.current = nein;
  });
  useEffect(() => {
    // Der Fokus kommt zurück, wo er war – meist der Knopf, der gefragt hat.
    const vorher = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    jaKnopf.current?.focus();
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") neinAktuell.current();
    };
    document.addEventListener("keydown", taste);
    return () => {
      document.removeEventListener("keydown", taste);
      vorher?.focus();
    };
  }, []);
  return (
    <div
      className="nachfrage-huelle crm-nachfrage"
      onClick={(e) => {
        if (e.target === e.currentTarget && !beschaeftigt) nein();
      }}
    >
      <div className={`nachfrage-fenster${gefahr ? " gefahr" : ""}`} role="alertdialog" aria-modal="true" aria-labelledby="nachfrage-titel">
        <span className="nachfrage-zeichen">
          <Ikon name={ikon} groesse="g" />
        </span>
        <h2 id="nachfrage-titel">{titel}</h2>
        {children && <div className="nachfrage-text">{children}</div>}
        <div className="crm-nachfrage-knoepfe">
          <button type="button" className="crm-knopf rand" onClick={nein} disabled={beschaeftigt}>
            {neinText}
          </button>
          <button type="button" ref={jaKnopf} className={`crm-knopf ${gefahr ? "rot" : "lime"}`} onClick={ja} disabled={beschaeftigt}>
            {beschaeftigt ? <span className="kreisel" aria-hidden="true" /> : null}
            {jaText}
          </button>
        </div>
      </div>
    </div>
  );
}
